import React from "react";
import { useTranslation } from "react-i18next";
import Seo from "../seo/Seo";
import CtaPair from "../components/CtaPair";
import { SITE_URL } from "../seo/site";
import {
  FaCar, FaDraftingCompass, FaGavel, FaQrcode,
  FaTags, FaReact, FaNodeJs, FaPython, FaServer, FaDatabase, FaCube, FaPencilRuler,
  FaBrain, FaCamera, FaMicrophoneAlt, FaGlobeEurope, FaLanguage, FaSearch, FaShieldAlt,
  FaMapMarkedAlt, FaBell, FaLock, FaMobileAlt, FaLayerGroup, FaExternalLinkAlt
} from "react-icons/fa";

// Code-side metadata per project; all texts live in the locale files (playground.projects)
const PROJECT_META = [
  {
    title: "EuroForm", icon: <FaCar className="service-icon" />, link: "https://euro-form.vercel.app", status: "Live",
    statIcons: [<FaTags className="text-info" />, <FaReact className="text-primary" />, <FaNodeJs className="text-success" />, <FaCamera className="text-warning" />, <FaMicrophoneAlt className="text-danger yolo-pulse" />, <FaGlobeEurope className="text-info" />]
  },
  {
    title: "my-build", icon: <FaDraftingCompass className="service-icon" />, link: "https://my-build.malilabs.com", status: "Live",
    statIcons: [<FaTags className="text-info" />, <FaReact className="text-primary" />, <FaPencilRuler className="text-warning" />, <FaCube className="text-success yolo-pulse" />, <FaBrain className="text-danger" />, <FaDatabase className="text-secondary" />]
  },
  {
    title: "otkucano", icon: <FaGavel className="service-icon" />, link: "https://otkucano.com", status: "Live",
    statIcons: [<FaTags className="text-info" />, <FaReact className="text-primary" />, <FaPython className="text-warning" />, <FaSearch className="text-success yolo-pulse" />, <FaLanguage className="text-danger" />, <FaShieldAlt className="text-secondary" />]
  },
  {
    title: "Perkly", icon: <FaQrcode className="service-icon" />, link: "https://perkly.malilabs.com", status: "Delivered",
    statIcons: [<FaTags className="text-info" />, <FaReact className="text-primary" />, <FaServer className="text-success" />, <FaDatabase className="text-warning" />, <FaMapMarkedAlt className="text-danger" />, <FaBell className="text-secondary yolo-pulse" />]
  }
];

const PILL_ICONS = [<FaLayerGroup />, <FaBrain />, <FaMobileAlt />, <FaLock />];

const Playground = () => {
  const { t } = useTranslation();
  const projects = t("playground.projects").map((txt, i) => ({
    ...PROJECT_META[i],
    ...txt,
    stats: txt.stats.map((stat, s) => ({ ...stat, icon: PROJECT_META[i].statIcons[s] }))
  }));
  const pills = t("playground.pills").map((label, i) => ({ label, icon: PILL_ICONS[i] }));
  const projectsJsonLd = {
    "@type": "ItemList",
    name: t("playground.title"),
    itemListElement: projects.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "SoftwareApplication",
        name: p.title,
        description: p.description,
        url: p.link || SITE_URL + "/playground",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        creator: { "@id": `${SITE_URL}/#organization` },
      },
    })),
  };

  const statusClass = (status) => {
    if (status === "Live") return "bg-success";
    if (status === "Beta" || status === "Private Beta") return "bg-warning text-dark";
    if (status === "Delivered") return "bg-primary";
    return "bg-secondary";
  };

  return (
    <div className="playground-page-container">
      <Seo page="playground" path="/playground" override={{ jsonLd: [projectsJsonLd] }} />
      {/* Hero Section */}
      <section className="work-hero text-center pt-5 pb-5 bg-gradient-modern text-white" style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%)' }}>
        <div className="container mt-5 pt-5 pb-4">
          <div className="heading_container d-flex justify-content-center mb-4">
            <h2 className="text-white border-bottom-0 pb-0" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>{t("playground.title")}</h2>
          </div>
          <p className="lead mt-3 mb-2 max-w-700 mx-auto text-light opacity-75" style={{ fontSize: '1.2rem' }}>
            {t("playground.lead")}
          </p>
          <div className="d-flex justify-content-center flex-wrap gap-3 mt-4">
            {pills.map((pill, i) => (
              <span key={i} className="badge rounded-pill px-3 py-2 bg-white text-dark d-inline-flex align-items-center shadow-sm" style={{ fontSize: '0.85rem', fontWeight: 600, gap: '8px' }}>
                <span className="text-primary">{pill.icon}</span>{pill.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="work-services py-5 bg-light">
        <div className="container">
          <div className="row g-4 justify-content-center">
            {projects.map((project, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <div className="modern-service-card h-100 p-4 shadow-sm border-0 rounded-4 transition-hover d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="icon-wrapper icon-gradient-bg rounded-circle d-inline-flex align-items-center justify-content-center p-3 yolo-spin-hover">
                      {project.icon}
                    </div>
                    <span className={`badge rounded-pill px-3 py-2 ${statusClass(project.status)}`}>
                      {t(`playground.status.${project.status}`)}
                    </span>
                  </div>
                  <h4 className="font-weight-bold mb-1">{project.title}</h4>
                  <p className="text-primary font-weight-bold mb-3" style={{ fontSize: '0.95rem', letterSpacing: '0.3px' }}>{project.tagline}</p>
                  <p className="text-muted lh-lg mb-3">{project.description}</p>

                  <ul className="list-unstyled mb-4">
                    {project.highlights.map((h, hIndex) => (
                      <li key={hIndex} className="d-flex align-items-start mb-2 small">
                        <span className="text-success me-2" style={{ marginRight: '8px', lineHeight: '1.4' }}>✓</span>
                        <span className="text-dark">{h}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Tech & Domain Specs Grid inside card */}
                  <div className="mt-auto mb-4 bg-white rounded-4 p-3 border shadow-sm">
                    <h6 className="font-weight-bold mb-3 text-uppercase small text-muted" style={{ letterSpacing: '1px' }}>{t("playground.specs")}</h6>
                    <div className="row g-2">
                      {project.stats.map((stat, sIndex) => (
                        <div key={sIndex} className="col-6 col-sm-4">
                          <div className="d-flex align-items-center p-2 rounded bg-light border h-100 yolo-cyber-pop" style={{ borderColor: 'rgba(0,0,0,0.05)' }}>
                            <div className="me-2 d-flex align-items-center justify-content-center" style={{ width: '24px', marginRight: '8px' }}>
                              {stat.icon}
                            </div>
                            <div className="text-truncate flex-grow-1">
                              <small className="d-block text-muted text-truncate" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.title}</small>
                              <span className="font-weight-bold small text-truncate d-block" title={stat.label}>{stat.label}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-2">
                    {project.link && (
                      <a href={project.link} target="_blank" rel="noopener noreferrer" className="cta-inline">
                        {t("playground.visit")} <FaExternalLinkAlt style={{ fontSize: '0.75rem' }} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="work-cta text-center py-5 bg-gradient-modern text-white" style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%)' }}>
        <div className="container py-4">
          <h2 className="mb-4 text-white">{t("playground.cta.title")}</h2>
          <p className="lead text-light opacity-75 mb-5 max-w-700 mx-auto">{t("playground.cta.text")}</p>
          <CtaPair tone="dark" align="center" />
        </div>
      </section>
    </div>
  );
};

export default Playground;
